import {
  FINANCE_AMOUNT_PATTERN,
  FINANCE_LINE_DESCRIPTION_MAX_LENGTH,
  FINANCE_QUANTITY_PATTERN,
  FINANCE_SIGNED_UNIT_PRICE_PATTERN,
  FINANCE_UNIT_PRICE_PATTERN,
} from '@/modules/finance/common/constants/line-item-validation.constants';

import { BillDetailsItemType } from '../types';

export enum BillLineValidationIssueCode {
  MissingProject = 'missingProject',
  MissingTimeOffType = 'missingTimeOffType',
  InvalidQuantity = 'invalidQuantity',
  InvalidUnitPrice = 'invalidUnitPrice',
  ZeroCalculatedLine = 'zeroCalculatedLine',
  EmptyDescription = 'emptyDescription',
  DescriptionTooLong = 'descriptionTooLong',
  InvalidAmount = 'invalidAmount',
}

export enum BillValidationIssueCode {
  EmptyItems = 'emptyItems',
  NegativeTotal = 'negativeTotal',
}

export const BILL_LINE_VALIDATION_FIELD = {
  EntityId: 'entityId',
  Description: 'description',
  Quantity: 'quantity',
  UnitPrice: 'unitPrice',
  Amount: 'amount',
} as const;

export type BillLineValidationField =
  (typeof BILL_LINE_VALIDATION_FIELD)[keyof typeof BILL_LINE_VALIDATION_FIELD];

export interface BillLineValidationIssue {
  code: BillLineValidationIssueCode;
  field: BillLineValidationField;
}

export interface BillLineValidationInput {
  entityType: BillDetailsItemType;
  entityId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  amount: string;
}

const isCalculatedLine = (entityType: BillDetailsItemType) =>
  entityType === BillDetailsItemType.Project || entityType === BillDetailsItemType.TimeOffType;

const isRateBasedLine = (entityType: BillDetailsItemType) =>
  isCalculatedLine(entityType) || entityType === BillDetailsItemType.Overhead;

const isOverheadLine = (entityType: BillDetailsItemType) =>
  entityType === BillDetailsItemType.Overhead;

const isFlatAmountLine = (entityType: BillDetailsItemType) =>
  entityType === BillDetailsItemType.Adjustment ||
  entityType === BillDetailsItemType.Client ||
  entityType === BillDetailsItemType.Unlinked;

export function getBillLineValidationIssues(
  item: BillLineValidationInput
): BillLineValidationIssue[] {
  const issues: BillLineValidationIssue[] = [];

  if (!/^\d+$/.test(item.entityId)) {
    if (item.entityType === BillDetailsItemType.Project) {
      issues.push({
        code: BillLineValidationIssueCode.MissingProject,
        field: BILL_LINE_VALIDATION_FIELD.EntityId,
      });
    }
    if (item.entityType === BillDetailsItemType.TimeOffType) {
      issues.push({
        code: BillLineValidationIssueCode.MissingTimeOffType,
        field: BILL_LINE_VALIDATION_FIELD.EntityId,
      });
    }
  }

  if (item.description.trim().length > FINANCE_LINE_DESCRIPTION_MAX_LENGTH) {
    issues.push({
      code: BillLineValidationIssueCode.DescriptionTooLong,
      field: BILL_LINE_VALIDATION_FIELD.Description,
    });
  }

  if (isCalculatedLine(item.entityType)) {
    if (!FINANCE_QUANTITY_PATTERN.test(item.quantity)) {
      issues.push({
        code: BillLineValidationIssueCode.InvalidQuantity,
        field: BILL_LINE_VALIDATION_FIELD.Quantity,
      });
    }
    if (!FINANCE_UNIT_PRICE_PATTERN.test(item.unitPrice)) {
      issues.push({
        code: BillLineValidationIssueCode.InvalidUnitPrice,
        field: BILL_LINE_VALIDATION_FIELD.UnitPrice,
      });
    }
    if (Number(item.quantity) === 0 && Number(item.unitPrice) === 0) {
      issues.push(
        {
          code: BillLineValidationIssueCode.ZeroCalculatedLine,
          field: BILL_LINE_VALIDATION_FIELD.Quantity,
        },
        {
          code: BillLineValidationIssueCode.ZeroCalculatedLine,
          field: BILL_LINE_VALIDATION_FIELD.UnitPrice,
        }
      );
    }
  }

  if (isOverheadLine(item.entityType) && item.description.trim() === '') {
    issues.push({
      code: BillLineValidationIssueCode.EmptyDescription,
      field: BILL_LINE_VALIDATION_FIELD.Description,
    });
  }

  if (isOverheadLine(item.entityType) && !FINANCE_SIGNED_UNIT_PRICE_PATTERN.test(item.unitPrice)) {
    issues.push({
      code: BillLineValidationIssueCode.InvalidUnitPrice,
      field: BILL_LINE_VALIDATION_FIELD.UnitPrice,
    });
  }

  if (isFlatAmountLine(item.entityType) && !FINANCE_AMOUNT_PATTERN.test(item.amount)) {
    issues.push({
      code: BillLineValidationIssueCode.InvalidAmount,
      field: BILL_LINE_VALIDATION_FIELD.Amount,
    });
  }

  return issues;
}

export const getBillLineAmount = (item: BillLineValidationInput): number => {
  const amount = isRateBasedLine(item.entityType)
    ? Number(item.quantity) * Number(item.unitPrice)
    : Number(item.amount);

  return Number.isFinite(amount) ? amount : 0;
};

export function getBillValidationIssues({
  items,
  monthlySalaryEnabled,
}: {
  items: BillLineValidationInput[];
  monthlySalaryEnabled: boolean;
}): BillValidationIssueCode[] {
  const issues: BillValidationIssueCode[] = [];

  if (!monthlySalaryEnabled && items.length === 0) {
    issues.push(BillValidationIssueCode.EmptyItems);
  }

  const total = items.reduce((sum, item) => sum + getBillLineAmount(item), 0);
  if (total < 0) issues.push(BillValidationIssueCode.NegativeTotal);

  return issues;
}
